"use client";

import { useEffect, useMemo, useRef, useState } from "react";

import { Icon } from "@/components/ui/icon";
import { Select } from "@/components/ui/select";
import { cn } from "@/lib/utils";

export interface InlineSearchOption {
  value: string;
  label: string;
}

type InlineSearchSelectProps = {
  options: InlineSearchOption[];
  placeholder?: string;
  className?: string;
  id?: string;
  "aria-label": string;
} & (
  | {
      multiple?: false;
      value: string;
      onChange: (value: string) => void;
    }
  | {
      multiple: true;
      value: string[];
      onChange: (value: string[]) => void;
    }
);

export function InlineSearchSelect(props: InlineSearchSelectProps) {
  const { options, placeholder = "Search...", className, id } = props;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedValues = props.multiple
    ? props.value
    : props.value
      ? [props.value]
      : [];
  const selected = useMemo(
    () =>
      selectedValues
        .map((value) => options.find((option) => option.value === value))
        .filter((option): option is InlineSearchOption => option != null),
    [options, selectedValues],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(q) ||
        option.value.toLowerCase().includes(q),
    );
  }, [options, query]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [filtered]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => inputRef.current?.focus(), 10);
    return () => clearTimeout(timer);
  }, [open]);

  function choose(value: string) {
    if (props.multiple) {
      const next = props.value.includes(value)
        ? props.value.filter((item) => item !== value)
        : [...props.value, value];
      props.onChange(next);
      setQuery("");
      return;
    }
    props.onChange(value);
    setQuery("");
    setOpen(false);
  }

  function remove(value: string) {
    if (!props.multiple) return;
    props.onChange(props.value.filter((item) => item !== value));
  }

  return (
    <Select.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setQuery("");
          setHighlightedIndex(-1);
        }
      }}
      highlightedIndex={highlightedIndex}
      onHighlightedIndexChange={setHighlightedIndex}
    >
      <Select.Trigger
        id={id}
        aria-label={props["aria-label"]}
        variant="filter"
        active={selected.length > 0}
        className={cn(
          "h-auto min-h-9 w-[280px] items-center py-1",
          className,
        )}
      >
        <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1 text-left">
          {props.multiple && selected.length > 0 ? (
            selected.map((option) => (
              <span
                key={option.value}
                className="inline-flex h-5 max-w-full min-w-0 items-center gap-0.5 rounded border border-border bg-contrast/6 px-1 text-xs text-heading"
              >
                <span className="truncate">{option.label}</span>
                <span
                  role="button"
                  aria-label={`Remove ${option.label}`}
                  className="inline-flex shrink-0 cursor-pointer opacity-40 hover:opacity-70"
                  onPointerDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    remove(option.value);
                  }}
                >
                  <Icon name="IconCrossMedium" size={12} className="text-heading" />
                </span>
              </span>
            ))
          ) : (
            <span
              className={cn(
                "truncate text-sm",
                selected.length > 0 ? "text-heading" : "text-contrast/50",
              )}
            >
              {selected[0]?.label ?? placeholder}
            </span>
          )}
        </span>
        <Select.Icon />
      </Select.Trigger>
      <Select.Content>
        <div className="flex h-9 shrink-0 items-center border-b border-contrast/8 px-2.5">
          <Icon
            name="IconMagnifyingGlass"
            size={14}
            className="mr-2 shrink-0 text-contrast opacity-40"
          />
          <Select.Input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={placeholder}
          />
        </div>
        <Select.List>
          {filtered.length === 0 ? (
            <Select.ListEmpty />
          ) : (
            filtered.map((option, index) => {
              const isSelected = selectedValues.includes(option.value);
              return (
                <Select.Item
                  key={option.value}
                  index={index}
                  selected={isSelected}
                  onSelect={() => choose(option.value)}
                >
                  <Select.ItemLabel selected={isSelected} className="text-left">
                    {option.label}
                  </Select.ItemLabel>
                  {isSelected ? <Select.ItemCheck /> : null}
                </Select.Item>
              );
            })
          )}
        </Select.List>
      </Select.Content>
    </Select.Root>
  );
}
